import { cn } from "@utils/tw"
import { Menu } from "@components/atoms/Menu"
import { Logo } from "@components/atoms/Logo"
import { Link, useRoute } from "eddev/routing"
import { SignupForm } from "@components/molecules/SignupForm"
import { useIsMobile } from "@hooks/useViewportDerived"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useState } from "react"
import { HomesNav } from "@components/atoms/HomePagesNavigation"
import { useHeaderVisibility } from "@hooks/useHeaderVisibility"
import { useNav } from "@hooks/useNav"
import { motion } from "framer-motion"
import { twMerge } from "tailwind-merge"

const HOMES_MENU_EXCLUDED_PATHS = ["electric-vehicle", "tool"]

export function Footer() {
  const isMobile = useIsMobile()
  const route = useRoute()
  const [showHomesMenu, setShowHomesMenu] = useState<boolean>(false)
  const navOpen = useNav()
  const { hidden } = useHeaderVisibility()

  useEffect(() => {
    if (HOMES_MENU_EXCLUDED_PATHS.some((path) => route.pathname?.includes(path))) {
      setShowHomesMenu(false)
    } else setShowHomesMenu(true)
  }, [route.pathname])

  return (
    <>
      <div
        className="px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto flex flex-col relative z-0"
        id="footer"
      >
        <SignupForm fueltrack={route.pathname === "/advocacy/explainers/fuel-excise-explained/"} />

        <FooterWrapper className={twMerge(showHomesMenu ? "mb-[88px] md:mb-3" : "mb-3")}>
          <Menu location="Main" showLevels={1} type={isMobile ? "accordion" : undefined} social />

          <div className="w-full flex flex-col gap-6 px-4 pt-8 [&_svg_path]:fill-white">
            <Logo />
            <div className="w-full flex flex-col md:flex-row md:justify-between hover:[&_a]:underline">
              <div className="flex flex-col md:flex-row gap-4 type-body-xs">
                <span>&copy; 2024 Australian Automobile Association</span>

                <div className="[&>ul]:flex [&>ul]:gap-4 [&>ul]:flex-col md:[&>ul]:flex-row [&>ul_svg]:hidden">
                  <Menu location="Legal" />
                </div>
              </div>

              <Link
                href="https://ed.studio/?utm_source=www.aaa.asn.au&utm_medium=website&utm_campaign=Site_by_ED"
                target="_blank"
                className="type-body-xs pt-4 md:pt-0"
              >
                Site by ED.
              </Link>
            </div>
          </div>
        </FooterWrapper>
      </div>

      {showHomesMenu && (
        <motion.div
          className={twMerge(
            "fixed bottom-0 z-10 w-full px-4 py-2 rounded-t-md bg-white lg:hidden",
            navOpen.open && "z-[1]",
          )}
          animate={{ y: hidden ? "100%" : "0%" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <HomesNav />
        </motion.div>
      )}
    </>
  )
}

const FOOTER_MENU_BASE = [
  "[&>ul]:flex [&>ul]:flex-col [&>ul]:gap-3",
  "[&>ul_li]:type-label-l [&>ul_li]:relative",

  "[&>ul_li>a]:pl-4 [&>ul_li>.current-page]:pl-4 [&>ul_li>a]:pr-3 [&>ul_li>.current-page]:pr-3 [&>ul_li>a]:h-[48px] [&>ul_li>.current-page]:h-[48px]",
  "[&>ul_li>a]:flex [&>ul_li>.current-page]:flex [&>ul_li>a]:items-center [&>ul_li>.current-page]:items-center [&>ul_li>a]:rounded-[6px] [&>ul_li>.current-page]:rounded-[6px]",
  "[&>ul_li>a]:mb-3 [&>ul_li>.current-page]:mb-3 [&>ul_li>a]:justify-between [&>ul_li>.current-page]:justify-between",
  "[&>ul_li>a]:transition-all [&>ul_li>.current-page]:transition-all [&>ul_li>a]:ease-movement [&>ul_li>.current-page]:ease-movement [&>ul_li>a]:duration-300 [&>ul_li>.current-page]:duration-300",
  "[&>ul_li>a_svg]:w-[32px] [&>ul_li>.current-page_svg]:w-[32px] [&>ul_li>a_svg]:h-[32px] [&>ul_li>.current-page_svg]:h-[32px]",
  "[&>ul_li>a_svg]:transition-all [&>ul_li>.current-page_svg]:transition-all [&>ul_li>a_svg]:ease-movement [&>ul_li>.current-page_svg]:ease-movement [&>ul_li>a_svg]:duration-300 [&>ul_li>.current-page_svg]:duration-300",
  "[&>ul_li>a:hover]:bg-white [&>ul_li>.current-page:hover]:bg-white [&>ul_li>a:hover]:text-black [&>ul_li>.current-page:hover]:text-black",
  "[&>ul_li>a.open_svg]:rotate-180 [&>ul_li>.current-page.open_svg]:rotate-180",
  "[&>ul_li>a.open+ul]:max-h-[100vh] [&>ul_li>.current-page.open+ul]:max-h-[100vh]",

  "[&>ul_li>ul]:max-h-0 [&>ul_li>ul]:overflow-hidden [&>ul_li>ul]:transition-all [&>ul_li>ul]:ease-movement [&>ul_li>ul]:duration-300",
  "[&>ul_li>ul_li]:type-label-s",
  "[&>ul_li>ul_li>.current-page]:h-[32px] [&>ul_li>ul_li>.current-page]:mb-0 [&>ul_li>ul_li>a]:h-[32px] [&>ul_li>ul_li>a]:mb-0",
  "[&>ul_li>ul_li>.current-page_svg]:hidden [&>ul_li>ul_li>a_svg]:hidden",

  "[&>ul>li:last-of-type>ul]:!h-auto",
]

const FOOTER_MENU_TABLET = [
  "md:[&>ul]:flex-row md:[&>ul]:flex-wrap md:[&>ul]:gap-y-6",
  "md:[&>ul_li]:w-[calc((100%-var(--spacing-3))*(1/2))]",
  "md:[&>ul_li_ul]:flex-col md:[&>ul_li_ul]:gap-y-3 md:[&>ul_li_ul]:max-h-none",
  "md:[&>ul_li_ul_li]:w-full",
]

const FOOTER_MENU_LAPTOP = [
  "lg:[&>ul]:grid lg:[&>ul]:gap-4 lg:[&>ul]:grid-cols-4",
  "lg:[&>ul>li]:!w-full lg:[&>ul>li]:!flex lg:[&>ul>li]:flex-col lg:[&>ul>li]:border-r lg:[&>ul>li]:border-white/10 lg:[&>ul>li]:!pr-4 lg:[&>ul>li:last-of-type]:border-none",
  "",
]

const footerWrapperStyles = cn(
  "w-full bg-black rounded-md py-6 px-4 mt-3 text-white",
  FOOTER_MENU_BASE,
  FOOTER_MENU_TABLET,
  FOOTER_MENU_LAPTOP,
)

const FooterWrapper = forwardRef<HTMLElement, ComponentPropsWithoutRef<"footer">>(({ className, ...props }, ref) => (
  <footer ref={ref} className={cn(footerWrapperStyles, className)} {...props} />
))
FooterWrapper.displayName = "FooterWrapper"
